import {
  type CreateSupportTicketRequest,
  SUPPORT_TICKET_STATUSES,
  type SupportAttachmentType,
  type SupportTicket,
  type SupportTicketAttachment,
  type SupportTicketComment,
  type SupportTicketDetail,
  type SupportTicketStatus,
} from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { enGB, nb } from "date-fns/locale";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  ImagePlus,
  Loader2,
  MessageSquare,
  Paperclip,
  RefreshCw,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState, useTransition } from "react";
import { Link } from "react-router";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { del, get, patch, post, postFormData } from "@/lib/api/_client";
import { markTicketRead } from "@/lib/api/support-tickets";
import { useSession } from "@/lib/auth-client";
import { useSidebar } from "@/lib/context/sidebar-context";
import { getBrowserDiagnostics } from "@/lib/diagnostics/browser-diagnostics";
import { useUserLanguage } from "@/lib/i18n";
import { useT } from "@/lib/i18n/use-t";
import { cn, distanceToNowFromDb } from "@/lib/utils";
import { T } from "./support-panel.i18n";

type View = "list" | "form" | "detail";

type ApiResult<T = void> = { success: boolean; data?: T; error?: string };

/**
 * Pending attachment staged from a `<SupportButton>` click on a host page.
 * The user toggles the checkbox in the New-ticket form; checked entries are
 * posted as attachments after the ticket is created.
 */
type PendingAttachment = {
  entityType: SupportAttachmentType;
  entityId: string;
  displayName: string;
  checked: boolean;
};

/** Custom-event payload dispatched by `<SupportButton>` to open the form. */
export type SupportOpenWithAttachmentDetail = {
  entityType: SupportAttachmentType;
  entityId: string;
  displayName: string;
};

const STATUS_VARIANT: Record<string, "info" | "secondary"> = {
  open: "info",
  closed: "secondary",
};

export function SupportPanel() {
  const { userLanguage } = useUserLanguage();
  const dateLocale = userLanguage === "nb_NO" ? nb : enGB;
  const t = useT(T);
  const statusLabel = (status: string) =>
    (t.statusLabel as Record<string, string>)[status] ?? status;

  const [isPending, startTransition] = useTransition();
  const { data: session } = useSession();
  const currentUserId = session?.user?.id || null;

  const [view, setView] = useState<View>("list");
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<SupportTicketStatus | "all">("all");
  const [filterTenantId, setFilterTenantId] = useState<string | "all">("all");
  const [isQuantfolio, setIsQuantfolio] = useState(false);

  // Form state
  const [formTitle, setFormTitle] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formImageKey, setFormImageKey] = useState<string | null>(null);
  // Object URL for the not-yet-submitted screenshot: there is no ticket to scope
  // a server fetch against until the form is posted.
  const [formImagePreview, setFormImagePreview] = useState<string | null>(null);
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Guards against an in-flight load's response landing after a newer one
  // (e.g. mount's load and a fast refresh-button click overlapping): only the
  // most recently started request is allowed to apply its result.
  const loadRequestIdRef = useRef(0);
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Detail state
  const [selected, setSelected] = useState<SupportTicketDetail | null>(null);
  const [newComment, setNewComment] = useState("");
  const [confirmDeleteTicket, setConfirmDeleteTicket] = useState(false);
  const [confirmDeleteCommentId, setConfirmDeleteCommentId] = useState<string | null>(null);
  const [confirmDeleteAttachmentId, setConfirmDeleteAttachmentId] = useState<string | null>(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  // Attachments staged from a SupportButton click on a host page.
  // Keyed by entityType so each entity can only be attached once per ticket.
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);

  const { openTab, openAiAgentThread, supportContext } = useSidebar();

  /**
   * Reload the list. `spinner` drives the full-panel skeleton on first load;
   * the refresh button spins its own icon instead so the list stays readable
   * while it updates.
   */
  const loadTickets = useCallback(async (spinner: "panel" | "button") => {
    const requestId = ++loadRequestIdRef.current;
    if (spinner === "panel") setIsLoading(true);
    else setIsRefreshing(true);
    setLoadError(null);
    try {
      const [data, qf] = await Promise.all([
        get<SupportTicket[]>("/internal/support-tickets"),
        get<{ isQuantfolio: boolean }>("/internal/support-tickets/is-quantfolio"),
      ]);
      // A newer load already started while this one was in flight — its
      // result (or the newer request still pending) wins, not this stale one.
      if (loadRequestIdRef.current !== requestId) return;
      setTickets(data);
      setIsQuantfolio(qf.isQuantfolio);
    } catch {
      if (loadRequestIdRef.current === requestId) setLoadError(t.loadFailed);
    } finally {
      if (loadRequestIdRef.current === requestId) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    loadTickets("panel");
  }, [loadTickets]);

  // Listen for SupportButton clicks: open the form view and stage the attachment as checked.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<SupportOpenWithAttachmentDetail>).detail;
      if (!detail?.entityType || !detail?.entityId) return;
      openTab("support");
      setView("form");
      stagePendingAttachment(detail);
    };
    window.addEventListener("support:open-with-attachment", handler);
    return () => window.removeEventListener("support:open-with-attachment", handler);
  }, [openTab]);

  const filteredTickets = tickets
    .filter((t) => filterStatus === "all" || t.status === filterStatus)
    .filter((t) => filterTenantId === "all" || t.tenantId === filterTenantId);

  /** Distinct tenants represented in the loaded ticket list — used to populate the tenant filter. */
  const tenantOptions = isQuantfolio
    ? Array.from(
        tickets
          .reduce((acc, t) => {
            if (t.tenant && !acc.has(t.tenant.id)) acc.set(t.tenant.id, t.tenant);
            return acc;
          }, new Map<string, NonNullable<SupportTicket["tenant"]>>())
          .values(),
      ).sort((a, b) => a.name.localeCompare(b.name))
    : [];

  // The screenshot is fetched through its TICKET, so the API can apply the same
  // visibility the ticket itself has. There is no way to ask for one by storage key.
  const getTicketImageUrl = (ticketId: string) =>
    `${process.env.BUN_PUBLIC_API_URL}/internal/support-tickets/${ticketId}/image`;

  // ─── Image upload ─────────────────────────────────────────

  const resizeImage = (file: File): Promise<Blob> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };
      img.onload = () => {
        const MAX = 1280;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) {
            height = Math.round((height * MAX) / width);
            width = MAX;
          } else {
            width = Math.round((width * MAX) / height);
            height = MAX;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("canvas"));
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("blob"))), "image/jpeg", 0.85);
      };
      img.onerror = () => reject(new Error("image load"));
      reader.onerror = () => reject(new Error("file read"));
      reader.readAsDataURL(file);
    });

  const uploadImage = async (file: File) => {
    const blob = await resizeImage(file);
    const fd = new FormData();
    fd.append("file", blob, "screenshot.jpg");
    const res = await postFormData<{ s3Key: string }>("/internal/support-tickets/upload-image", fd);
    return res.s3Key;
  };

  /** Drop the uploaded key and release the object URL behind its preview. */
  const clearFormImage = () => {
    setFormImageKey(null);
    setFormImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  /** Validate, upload and preview one image — shared by the picker and by drop. */
  const acceptImage = async (file: File) => {
    if (!file.type.startsWith("image/")) return setFormError(t.selectImage);
    if (file.size > 2 * 1024 * 1024) return setFormError(t.imageTooLarge);
    setFormError(null);
    setIsUploading(true);
    try {
      const key = await uploadImage(file);
      setFormImageKey(key);
      setFormImagePreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(file);
      });
    } catch {
      setFormError(t.uploadFailed);
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await acceptImage(file);
    e.target.value = "";
  };

  const handleImageDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingImage(false);
    const file = e.dataTransfer.files?.[0];
    if (file) await acceptImage(file);
  };

  // ─── Submit / detail flow ─────────────────────────────────

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!formTitle.trim()) return setFormError(t.titleRequired);
    if (!formDescription.trim()) return setFormError(t.descriptionRequired);

    startTransition(async () => {
      // Attachments go in the create payload, so the ticket and its context are
      // written together. Posting them afterwards left a window in which the
      // sync bridge could mirror out a ticket with none of them attached.
      const result = await post<ApiResult<SupportTicket>>("/internal/support-tickets", {
        title: formTitle,
        description: formDescription,
        imageUrl: formImageKey,
        browserInfo: getBrowserDiagnostics(),
        attachments: pendingAttachments
          .filter((a) => a.checked)
          .map((a) => ({ entityType: a.entityType, entityId: a.entityId })),
      } satisfies CreateSupportTicketRequest);
      if (!result.success || !result.data) {
        setFormError(result.error || t.submitFailed);
        return;
      }
      const newTicket = result.data;
      setTickets((prev) => [newTicket, ...prev]);
      setFormTitle("");
      setFormDescription("");
      clearFormImage();
      setPendingAttachments([]);
      setView("list");
    });
  };

  const openDetail = async (ticketId: string) => {
    setView("detail");
    setShowDiagnostics(false);
    try {
      const detail = await get<SupportTicketDetail>(`/internal/support-tickets/${ticketId}`);
      setSelected(detail);
      // Opening the ticket is what counts as reading it. Failing to record that
      // must not break the view — the badge simply stays until the next open.
      try {
        // Mark read only through the last comment this response actually
        // carried — not "now" — so a comment landing in between still badges.
        await markTicketRead(ticketId, detail.comments.at(-1)?.id);
        queryClient.invalidateQueries({ queryKey: ["support-tickets", "unread-count"] });
      } catch {
        /* badge is advisory */
      }
    } catch {
      setSelected(null);
    }
  };

  // QL-728 — a support-reply notification opens its ticket here (see openSupportTicket).
  const openDetailRef = useRef(openDetail);
  openDetailRef.current = openDetail;
  useEffect(() => {
    const handler = (e: Event) => {
      const ticketId = (e as CustomEvent<{ ticketId?: string }>).detail?.ticketId;
      if (ticketId) openDetailRef.current(ticketId);
    };
    window.addEventListener("support:open-ticket", handler);
    return () => window.removeEventListener("support:open-ticket", handler);
  }, []);

  const handleStatusChange = (status: SupportTicketStatus) => {
    if (!selected) return;
    startTransition(async () => {
      const res = await patch<ApiResult<SupportTicket>>(
        `/internal/support-tickets/${selected.ticket.id}`,
        { status },
      );
      if (res.success && res.data) {
        setSelected({
          ...selected,
          ticket: { ...selected.ticket, ...res.data, browserInfo: selected.ticket.browserInfo },
        });
        setTickets((prev) => prev.map((t) => (t.id === res.data!.id ? res.data! : t)));
      }
    });
  };

  const handleAddComment = () => {
    if (!selected || !newComment.trim()) return;
    startTransition(async () => {
      const res = await post<ApiResult<SupportTicketComment>>(
        `/internal/support-tickets/${selected.ticket.id}/comments`,
        { content: newComment.trim() },
      );
      if (res.success && res.data) {
        setSelected({
          ...selected,
          comments: [...selected.comments, res.data],
          ticket: { ...selected.ticket, commentCount: selected.ticket.commentCount + 1 },
        });
        setTickets((prev) =>
          prev.map((t) =>
            t.id === selected.ticket.id ? { ...t, commentCount: t.commentCount + 1 } : t,
          ),
        );
        setNewComment("");
      }
    });
  };

  const handleDeleteComment = (commentId: string) => {
    if (!selected) return;
    startTransition(async () => {
      const res = await del<ApiResult>(`/internal/support-tickets/comments/${commentId}`);
      if (res.success) {
        setSelected({
          ...selected,
          comments: selected.comments.filter((c) => c.id !== commentId),
          ticket: { ...selected.ticket, commentCount: selected.ticket.commentCount - 1 },
        });
        setTickets((prev) =>
          prev.map((t) =>
            t.id === selected.ticket.id ? { ...t, commentCount: t.commentCount - 1 } : t,
          ),
        );
      }
      setConfirmDeleteCommentId(null);
    });
  };

  const handleDeleteTicket = () => {
    if (!selected) return;
    startTransition(async () => {
      const res = await del<ApiResult>(`/internal/support-tickets/${selected.ticket.id}`);
      if (res.success) {
        setTickets((prev) => prev.filter((t) => t.id !== selected.ticket.id));
        setSelected(null);
        setView("list");
      }
      setConfirmDeleteTicket(false);
    });
  };

  const handleDeleteAttachment = (attachmentId: string) => {
    if (!selected) return;
    startTransition(async () => {
      const res = await del<ApiResult>(`/internal/support-tickets/attachments/${attachmentId}`);
      if (res.success) {
        setSelected({
          ...selected,
          attachments: selected.attachments.filter((a) => a.id !== attachmentId),
          ticket: { ...selected.ticket, attachmentCount: selected.ticket.attachmentCount - 1 },
        });
      }
      setConfirmDeleteAttachmentId(null);
    });
  };

  const canDeleteOwn = (createdById: string) => isQuantfolio || createdById === currentUserId;

  /** Dedupe-add an attachment entry to the pending list with checked=true. */
  const stagePendingAttachment = (detail: SupportOpenWithAttachmentDetail) => {
    setPendingAttachments((prev) => {
      const without = prev.filter(
        (p) => !(p.entityType === detail.entityType && p.entityId === detail.entityId),
      );
      return [
        ...without,
        {
          entityType: detail.entityType,
          entityId: detail.entityId,
          displayName: detail.displayName,
          checked: true,
        },
      ];
    });
  };

  /** Switch to form view and stage the current page's support context (if any) as a pending attachment. */
  const openNewTicketForm = () => {
    setView("form");
    setPendingAttachments(
      supportContext
        ? [
            {
              entityType: supportContext.entityType,
              entityId: supportContext.entityId,
              displayName: supportContext.displayName,
              checked: true,
            },
          ]
        : [],
    );
  };

  /**
   * Build a URL to navigate back to the live entity from an attachment row.
   * Returns null for entity types that have no addressable page (e.g. chat threads
   * are handled via the sidebar's openAiAgentThread callback rather than a URL).
   */
  const getAttachmentHref = (a: SupportTicketAttachment): string | null => {
    if (a.entityType === "flow_instance") {
      const clientId = a.snapshot.data?.clientId as string | undefined;
      return clientId ? `/clients/${clientId}/flows/${a.entityId}` : null;
    }
    return null;
  };

  // ─── Render: form view ────────────────────────────────────

  if (view === "form") {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-2 px-6 h-16">
          <Button
            variant="ghost"
            size="icon"
            tooltip={t.back}
            onClick={() => setView("list")}
            className="p-1"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-xl font-semibold">{t.newTicket}</h2>
        </div>
        <Separator />
        <ScrollArea className="flex-1">
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {formError && (
              <div className="text-sm text-destructive bg-destructive/10 p-2 rounded-md">
                {formError}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="title" className="text-sm">
                {t.titleLabel} *
              </Label>
              <Input
                id="title"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder={t.titlePlaceholder}
                maxLength={200}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm">
                {t.descLabel} *
              </Label>
              <Textarea
                id="description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder={t.descPlaceholder}
                maxLength={4000}
                rows={6}
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm">{t.screenshot}</Label>
              {formImageKey ? (
                <div className="relative">
                  <img src={formImagePreview ?? ""} alt="" className="w-full rounded-md border" />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={clearFormImage}
                  >
                    {t.removeImage}
                  </Button>
                </div>
              ) : (
                <label
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDraggingImage(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDraggingImage(false);
                  }}
                  onDrop={handleImageDrop}
                  className={cn(
                    "flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-md cursor-pointer hover:bg-muted/50",
                    isDraggingImage && "border-primary bg-muted/50",
                    isUploading && "opacity-50 pointer-events-none",
                  )}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                    disabled={isUploading}
                  />
                  {isUploading ? (
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  ) : (
                    <>
                      <ImagePlus className="h-6 w-6 text-muted-foreground" />
                      <span className="text-xs mt-1 text-muted-foreground">{t.screenshotHint}</span>
                    </>
                  )}
                </label>
              )}
            </div>

            {pendingAttachments.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm">{t.attachContext}</Label>
                <div className="space-y-1 rounded-md border bg-muted/30 p-2">
                  {pendingAttachments.map((p, i) => (
                    <label
                      key={`${p.entityType}:${p.entityId}`}
                      className="flex items-center gap-2 text-xs cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={p.checked}
                        onChange={(e) =>
                          setPendingAttachments((prev) =>
                            prev.map((q, idx) =>
                              idx === i ? { ...q, checked: e.target.checked } : q,
                            ),
                          )
                        }
                        className="h-4 w-4"
                      />
                      <Badge variant="outline" className="text-[10px]">
                        {t.attachmentType[p.entityType]}
                      </Badge>
                      <span className="font-medium truncate">{p.displayName}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="text-xs text-muted-foreground rounded-md border bg-muted/30 p-2">
              {t.diagnosticsHint}
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setView("list")}
                disabled={isPending}
                className="flex-1"
              >
                {t.cancel}
              </Button>
              <Button type="submit" className="flex-1" loading={isPending}>
                {t.submit}
              </Button>
            </div>
          </form>
        </ScrollArea>
      </div>
    );
  }

  // ─── Render: detail view ──────────────────────────────────

  if (view === "detail" && selected) {
    const ticket = selected.ticket;
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-2 px-6 h-16">
          <Button
            variant="ghost"
            size="icon"
            tooltip={t.back}
            onClick={() => setView("list")}
            className="p-1"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-xl font-semibold truncate flex-1">{ticket.title}</h2>
          {canDeleteOwn(ticket.createdById) && (
            <Button
              variant="ghost"
              size="icon"
              tooltip={t.delete}
              onClick={() => setConfirmDeleteTicket(true)}
              disabled={isPending}
              className="text-destructive p-1"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
        <Separator />
        {/* !block: keeps the content box at panel width, so a long URL in a
            comment or the diagnostics dump cannot stretch the screenshot. */}
        <ScrollArea className="flex-1" viewportClassName="[&>div]:!block">
          <div className="p-6 space-y-4">
            {/* Status row — badge + tenant badge (QF admin only) + Close/Reopen for author or QF */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">{t.status}:</span>
              <Badge variant={STATUS_VARIANT[ticket.status] ?? "secondary"} className="text-[10px]">
                {statusLabel(ticket.status)}
              </Badge>
              {isQuantfolio && ticket.tenant && (
                <Badge variant="outline" className="text-[10px]">
                  {ticket.tenant.name}
                </Badge>
              )}
              {canDeleteOwn(ticket.createdById) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs ml-auto"
                  onClick={() => handleStatusChange(ticket.status === "open" ? "closed" : "open")}
                  disabled={isPending}
                >
                  {ticket.status === "open" ? t.close : t.reopen}
                </Button>
              )}
            </div>

            {/* Description */}
            <p className="text-sm whitespace-pre-wrap break-words">{ticket.description}</p>

            {/* Screenshot */}
            {ticket.imageUrl && (
              <img src={getTicketImageUrl(ticket.id)} alt="" className="w-full rounded-md border" />
            )}

            {/* Author */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Avatar className="h-5 w-5">
                <AvatarImage src={ticket.createdBy.image || undefined} />
                <AvatarFallback className="text-[10px]">
                  {ticket.createdBy.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span>{ticket.createdBy.name}</span>
              <span>·</span>
              <span>
                {distanceToNowFromDb(ticket.createdAt, {
                  addSuffix: true,
                  locale: dateLocale,
                })}
              </span>
            </div>

            <Separator />

            {/* Attachments — read-only, captured at create-time via SupportButton */}
            {selected.attachments.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-medium text-sm flex items-center gap-2">
                  <Paperclip className="h-4 w-4" />
                  {t.attachments} ({selected.attachments.length})
                </h3>
                <div className="space-y-1">
                  {selected.attachments.map((a) => {
                    const href = getAttachmentHref(a);
                    const inner = (
                      <>
                        <Badge variant="outline" className="text-[10px] mr-2">
                          {t.attachmentType[a.entityType]}
                        </Badge>
                        <span className="font-medium truncate">{a.snapshot.displayName}</span>
                        <ExternalLink className="h-3 w-3 ml-1 text-muted-foreground" />
                      </>
                    );
                    return (
                      <div
                        key={a.id}
                        className="flex items-center justify-between text-xs rounded-md border bg-muted/30 px-2 py-1.5 group"
                      >
                        {href ? (
                          <Link
                            to={href}
                            className="flex items-center min-w-0 hover:text-foreground"
                          >
                            {inner}
                          </Link>
                        ) : a.entityType === "chat_thread" ? (
                          <button
                            type="button"
                            onClick={() => openAiAgentThread(a.entityId)}
                            className="flex items-center min-w-0 hover:text-foreground text-left"
                          >
                            {inner}
                          </button>
                        ) : (
                          <div className="flex items-center min-w-0">{inner}</div>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          tooltip={t.remove}
                          className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                          onClick={() => setConfirmDeleteAttachmentId(a.id)}
                          disabled={isPending}
                          aria-label={t.delete}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <Separator />

            {/* Diagnostics fold-out */}
            {ticket.browserInfo && (
              <div className="space-y-2">
                <button
                  type="button"
                  className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                  onClick={() => setShowDiagnostics((v) => !v)}
                >
                  {showDiagnostics ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                  {t.browserInfo}
                </button>
                {showDiagnostics && (
                  <pre className="text-[10px] bg-muted/30 rounded p-2 overflow-auto max-h-60 whitespace-pre-wrap break-words">
                    {JSON.stringify(ticket.browserInfo, null, 2)}
                  </pre>
                )}
              </div>
            )}

            <Separator />

            {/* Comments */}
            <div className="space-y-3">
              <h3 className="font-medium text-sm flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                {t.comments} ({selected.comments.length})
              </h3>

              {selected.comments.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t.noComments}</p>
              ) : (
                <div className="space-y-3">
                  {selected.comments.map((c) => (
                    <div key={c.id} className="flex gap-2 group">
                      <Avatar className="h-6 w-6 flex-shrink-0">
                        <AvatarImage src={c.createdBy.image || undefined} />
                        <AvatarFallback className="text-[10px]">
                          {c.createdBy.name.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium">{c.createdBy.name}</span>
                          <span className="text-[10px] text-muted-foreground">
                            {distanceToNowFromDb(c.createdAt, {
                              addSuffix: true,
                              locale: dateLocale,
                            })}
                          </span>
                          {canDeleteOwn(c.createdById) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              tooltip={t.remove}
                              className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                              onClick={() => setConfirmDeleteCommentId(c.id)}
                              disabled={isPending}
                              aria-label={t.delete}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        <p className="text-sm mt-0.5 whitespace-pre-wrap break-words">
                          {c.content}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={t.addComment}
                  rows={2}
                  maxLength={2000}
                  className="flex-1 resize-none text-sm"
                />
                <Button
                  size="icon"
                  tooltip={t.postComment}
                  onClick={handleAddComment}
                  disabled={!newComment.trim()}
                  className="self-end"
                  loading={isPending}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </ScrollArea>

        <ConfirmDialog
          open={confirmDeleteTicket}
          onOpenChange={setConfirmDeleteTicket}
          title={t.delete}
          description={t.confirmDelete}
          confirmText={t.delete}
          cancelText={t.cancel}
          onConfirm={handleDeleteTicket}
          variant="destructive"
        />
        <ConfirmDialog
          open={!!confirmDeleteCommentId}
          onOpenChange={(o) => !o && setConfirmDeleteCommentId(null)}
          title={t.delete}
          description={t.confirmDeleteComment}
          confirmText={t.delete}
          cancelText={t.cancel}
          onConfirm={() => confirmDeleteCommentId && handleDeleteComment(confirmDeleteCommentId)}
          variant="destructive"
        />
        <ConfirmDialog
          open={!!confirmDeleteAttachmentId}
          onOpenChange={(o) => !o && setConfirmDeleteAttachmentId(null)}
          title={t.delete}
          description={t.confirmDeleteAttachment}
          confirmText={t.delete}
          cancelText={t.cancel}
          onConfirm={() =>
            confirmDeleteAttachmentId && handleDeleteAttachment(confirmDeleteAttachmentId)
          }
          variant="destructive"
        />
      </div>
    );
  }

  // ─── Render: list view ────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 h-16">
        <h2 className="text-xl font-semibold">{t.title}</h2>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => loadTickets("button")}
          disabled={isLoading}
          loading={isRefreshing}
          tooltip={t.refresh}
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
      <Separator />

      <div className="px-6 py-3 flex-shrink-0 flex gap-2">
        <Select
          value={filterStatus}
          onValueChange={(v) => setFilterStatus(v as SupportTicketStatus | "all")}
        >
          <SelectTrigger className="h-8 text-xs flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.filterAll}</SelectItem>
            {SUPPORT_TICKET_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {statusLabel(s)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isQuantfolio && tenantOptions.length > 1 && (
          <Select value={filterTenantId} onValueChange={(v) => setFilterTenantId(v)}>
            <SelectTrigger className="h-8 text-xs flex-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.filterAllTenants}</SelectItem>
              {tenantOptions.map((tn) => (
                <SelectItem key={tn.id} value={tn.id}>
                  {tn.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <Separator />

      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : loadError ? (
          <div className="p-6 text-center text-destructive text-sm">{loadError}</div>
        ) : filteredTickets.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground text-sm">{t.noTickets}</div>
        ) : (
          <div className="divide-y">
            {filteredTickets.map((t) => (
              // biome-ignore lint/a11y/useSemanticElements: list row pattern matches IdeasPanel
              <div
                key={t.id}
                role="button"
                tabIndex={0}
                className="p-3 hover:bg-muted/50 cursor-pointer focus:outline-none focus:bg-muted/50"
                onClick={() => openDetail(t.id)}
                onKeyDown={(e) => e.key === "Enter" && openDetail(t.id)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium text-sm truncate">{t.title}</h4>
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                      {t.description}
                    </p>
                    <div className="flex flex-wrap items-center gap-1 mt-1.5">
                      <Badge
                        variant={STATUS_VARIANT[t.status] ?? "secondary"}
                        className="text-[10px] px-1.5 py-0"
                      >
                        {statusLabel(t.status)}
                      </Badge>
                      {isQuantfolio && t.tenant && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {t.tenant.name}
                        </Badge>
                      )}
                      {t.attachmentCount > 0 && (
                        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5 ml-1">
                          <Paperclip className="h-3 w-3" />
                          {t.attachmentCount}
                        </span>
                      )}
                      <span className="text-[10px] text-muted-foreground flex items-center gap-0.5 ml-1">
                        <MessageSquare className="h-3 w-3" />
                        {t.commentCount}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      <div className="px-6 py-3 flex-shrink-0 border-t">
        <Button size="sm" onClick={openNewTicketForm} className="w-full">
          {t.newTicket}
        </Button>
      </div>
    </div>
  );
}
